/**
 * =============================================================================
 * Login Page
 * =============================================================================
 *
 * Landing page for unauthenticated users.
 * Supports local login and Keycloak login.
 */

import { useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { at } from '../utils/appAdminI18n'

const LoginPage: React.FC = () => {
  const { login, loginLocal, sessionEndedReason, dismissSessionNotice } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleLocalLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)
    const result = await loginLocal(username, password)
    setIsSubmitting(false)
    if (!result.ok) {
      setError(result.error || 'Local login failed')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-600 to-primary-800">
      <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md space-y-6">
        {/* Logo & Title */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">RehaPiano</h1>
          <p className="text-gray-600">Admin Portal</p>
        </div>

        {/* Session ended notice (FE issue #24): a reload or an expired token
            used to drop the user here with no explanation whatsoever. */}
        {sessionEndedReason && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
            role="status"
          >
            <div className="flex justify-between items-start gap-3">
              <div>
                <h2 className="text-sm font-semibold text-amber-900">
                  {at('sessionExpiredTitle')}
                </h2>
                <p className="text-sm text-amber-800 mt-1">
                  {sessionEndedReason === 'restore_failed'
                    ? at('sessionRestoreFailedBody')
                    : at('sessionExpiredBody')}
                </p>
              </div>
              <button
                type="button"
                onClick={dismissSessionNotice}
                className="text-amber-700 hover:text-amber-900 text-sm"
                aria-label={at('close')}
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Local Login */}
        <form className="space-y-3" onSubmit={handleLocalLogin}>
          <div>
            <label htmlFor="local-username" className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              id="local-username"
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="local-admin"
              autoComplete="username"
              required
            />
          </div>
          <div>
            <label htmlFor="local-password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              id="local-password"
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="w-full btn-primary py-3"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Signing in...' : 'Sign In Locally'}
          </button>
        </form>

        <div className="relative">
          <div className="border-t border-gray-200" />
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white px-2 text-xs text-gray-500">
            OR
          </span>
        </div>

        <button
          onClick={login}
          className="w-full btn-secondary py-3"
        >
          Sign In with Keycloak
        </button>

        {/* Help Text */}
        <p className="text-center text-xs text-gray-500">
          Contact your system administrator if you need access.
        </p>
      </div>
    </div>
  )
}

export default LoginPage
